import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Box,
  Stack,
  Divider,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  InputAdornment,
  Select,
  MenuItem,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WifiIcon from "@mui/icons-material/Wifi";
import { useLocation, useNavigate } from "react-router-dom";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import StarIcon from "@mui/icons-material/Star";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import GoogleIcon from "../images/google_image.png";
import { searchAPI } from "../API/Seach";
import Loader from "../Components/Loader";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Modal from "@mui/material/Modal";
import { tokens } from "../theme/tokens";

const Payment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    bookingDetails: initialBookingDetails,
    hotelInfo: passedHotelInfo,
    selectedRoomSummary,
  } = location.state || {};

  // Add state for booking details
  const [bookingDetails, setBookingDetails] = useState(initialBookingDetails);
  // Add new state for multiple guest details
  const [guestDetails, setGuestDetails] = useState([]);
  const [title, setTitle] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [email, setEmail] = useState("");
  const [panCardNumber, setPanCardNumber] = useState("");
  const [passportNumber, setPassportNumber] = useState("");
  const [passportExpiry, setPassportExpiry] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [country, setCountry] = useState("India");
  const [state, setState] = useState("");
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const [openCancellationPolicy, setOpenCancellationPolicy] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");

  // Initialize guest details when booking details change (from details.room or from query.occupancies for select-API flow)
  useEffect(() => {
    if (bookingDetails?.details?.room) {
      const initialGuestDetails = bookingDetails.details.room.flatMap((room, roomIndex) => {
        const roomGuests = [];
        let guestCounter = 1;
        for (let i = 0; i < room.occupancy.numOfAdults; i++) {
          roomGuests.push({
            roomIndex,
            guestType: 'adult',
            guestNumber: guestCounter++,
            title: "",
            firstName: "",
            lastName: "",
            mobileNumber: "",
            email: "",
            panCardNumber: "",
            passportNumber: "",
            passportExpiry: "",
            age: null
          });
        }
        for (let i = 0; i < room.occupancy.numOfChildren; i++) {
          roomGuests.push({
            roomIndex,
            guestType: 'child',
            guestNumber: guestCounter++,
            title: "",
            firstName: "",
            lastName: "",
            mobileNumber: "",
            email: "",
            panCardNumber: "",
            passportNumber: "",
            passportExpiry: "",
            age: ""
          });
        }
        return roomGuests;
      });
      setGuestDetails(initialGuestDetails);
    } else if (bookingDetails?.query?.occupancies?.length) {
      const initialGuestDetails = bookingDetails.query.occupancies.flatMap((occ, roomIndex) => {
        const roomGuests = [];
        let guestCounter = 1;
        const adults = occ.numOfAdults || 0;
        const childAges = occ.childAges || [];
        for (let i = 0; i < adults; i++) {
          roomGuests.push({
            roomIndex,
            guestType: 'adult',
            guestNumber: guestCounter++,
            title: "",
            firstName: "",
            lastName: "",
            mobileNumber: "",
            email: "",
            panCardNumber: "",
            passportNumber: "",
            passportExpiry: "",
            age: null
          });
        }
        for (let i = 0; i < childAges.length; i++) {
          roomGuests.push({
            roomIndex,
            guestType: 'child',
            guestNumber: guestCounter++,
            title: "",
            firstName: "",
            lastName: "",
            mobileNumber: "",
            email: "",
            panCardNumber: "",
            passportNumber: "",
            passportExpiry: "",
            age: childAges[i] ?? ""
          });
        }
        return roomGuests;
      });
      setGuestDetails(initialGuestDetails);
    }
  }, [bookingDetails]);

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format date with time helper function
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format currency helper function
  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    })
      .format(amount)
      .replace("₹", "₹");
  };

  const checkInDate = formatDate(bookingDetails?.query?.checkIn);
  const checkOutDate = formatDate(bookingDetails?.query?.checkOut);

  const handleProceedToPay = async (paymentMethod) => {
    try {
      setIsLoading(true);

      const isAnyGuestInvalid = guestDetails.some(
        guest => !guest.firstName || !guest.lastName || (guest.guestType === 'child' && !guest.age)
      );

      if (isAnyGuestInvalid) {
        toast.warning("Please fill in all required guest details");
        setIsLoading(false);
        return;
      }

      if (bookingDetails?.details?.rules?.isPassportMandatory) {
        const isAnyPassportInvalid = guestDetails.some(
          guest => !guest.passportNumber || !guest.passportExpiry
        );
        if (isAnyPassportInvalid) {
          toast.warning("Passport details are mandatory for all guests");
          setIsLoading(false);
          return;
        }
      }

      if (!bookingDetails?.details?.room?.length) {
        toast.info("Booking order for this flow is not yet configured. Please use the flow from search results.");
        setIsLoading(false);
        return;
      }

      const bookingOrderPayload = {
        ...bookingDetails,
        query: {
          ...bookingDetails.query,
          pgName: paymentMethod
        },
        roomAllocation: bookingDetails.details.room.map((room, roomIndex) => {
          const roomGuests = guestDetails.filter(guest => guest.roomIndex === roomIndex);
          return {
            roomId: room.roomId,
            rateId: room.rateId,
            guests: roomGuests.map(guest => ({
              title: guest.title,
              firstName: guest.firstName,
              lastName: guest.lastName,
              email: guest.email,
              isLeadGuest: guest.roomIndex === 0 && guest.guestNumber === 1,
              age: guest.guestType === 'child' ? parseInt(guest.age) : null,
              type: guest.guestType,
              isdCode: null,
              contactNumber: guest.mobileNumber,
              panCardNumber: guest.panCardNumber,
              passportNumber: guest.passportNumber,
              passportExpiry: guest.passportExpiry
            })),
            paymentMethod: paymentMethod
          };
        }),
        specialRequests: "string"
      };

      // Create booking order
      const bookingResponse = await searchAPI.bookingOrder(bookingOrderPayload);

      if (bookingResponse?.status && bookingResponse?.code === 200) {
        // If payment method is HDFC and payment link is present, show in iframe (leave as before)
        // For all, pass paymentMethod in navigation state
        navigate('/payment-status', { 
          state: { 
            bookingOrder: bookingResponse.data,
            paymentMethod: paymentMethod
          } 
        });
      } else {
        toast.error(bookingResponse?.message || "Failed to create booking order");
      }
    } catch (error) {
      console.error("Booking order error:", error);
      toast.error(error?.response?.data?.message || "Failed to create booking order. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyCoupon = async (code) => {
    try {
      setIsCouponLoading(true);
      const couponToApply = code || couponCode;
      const selectedCoupon = availableCoupons?.find((coupon) => coupon.couponCode === couponToApply) || null;

      const payload = {
        ...bookingDetails,
        details: {
          ...bookingDetails.details,
          coupon: {
            couponCode: couponToApply,
            couponId: selectedCoupon?.couponId || 0,
            couponName: selectedCoupon?.couponName || null,
            categorySymbol: selectedCoupon?.categorySymbol || null,
            couponValue: selectedCoupon?.couponValue || 0
          }
        }
      };

      const response = await searchAPI.applyCoupon(payload);
      setBookingDetails(response.data);
      toast.success("Coupon applied successfully!");
    } catch (error) {
      console.error("Coupon application error:", error);
      toast.error(error?.response?.data?.message || "Failed to apply coupon. Please try again.");
    } finally {
      setIsCouponLoading(false);
    }
  };

  useEffect(() => {
    const fetchCoupon = async () => {
      try {
        const response = await searchAPI.getCoupons();
        if (response.status && response.code === 200) {
          setAvailableCoupons(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch coupon:", error);
      }
    };

    fetchCoupon();
  }, []);

  // New function to handle validation before opening payment modal
  const handleProceedToPayClick = () => {
    // Validate all guest details
    const isAnyGuestInvalid = guestDetails.some(
      guest => !guest.firstName || !guest.lastName || (guest.guestType === 'child' && !guest.age)
    );

    if (isAnyGuestInvalid) {
      toast.warning("Please fill in all required guest details");
      return;
    }
    setPaymentModalOpen(true);
  };

  const hasDetailsFlow = Boolean(bookingDetails?.details);
  const hasSelectFlow = Boolean(bookingDetails?.query && (passedHotelInfo || selectedRoomSummary));

  // Early return only when we have no booking data at all
  if (!bookingDetails) {
    return (
      <Container maxWidth={false} sx={{ p: 4 }}>
        <Typography variant="h6">Loading booking details...</Typography>
      </Container>
    );
  }

  const hotelName = hasDetailsFlow
    ? bookingDetails?.details?.hotelDetails?.hotelName
    : passedHotelInfo?.name;
  const starRating = hasDetailsFlow
    ? Number(bookingDetails?.details?.hotelDetails?.starRating) || 0
    : Number(passedHotelInfo?.starRating) || 0;
  const reviewRating = hasDetailsFlow
    ? bookingDetails?.details?.hotelDetails?.reviewRating
    : passedHotelInfo?.reviews?.rating;
  const reviewCount = hasDetailsFlow
    ? bookingDetails?.details?.hotelDetails?.reviewCount
    : passedHotelInfo?.reviews?.count;
  const addressLine = hasDetailsFlow
    ? `${bookingDetails?.details?.contact?.address?.line1 || ''}, ${bookingDetails?.details?.contact?.address?.line2 || ''}, ${bookingDetails?.details?.contact?.address?.postalCode || ''} ${bookingDetails?.details?.contact?.address?.city?.name || ''}, ${bookingDetails?.details?.contact?.address?.country?.name || ''}`
    : passedHotelInfo?.address
      ? [passedHotelInfo.address.line1, passedHotelInfo.address.city, passedHotelInfo.address.country].filter(Boolean).join(", ")
      : "";
  const totalAmount = hasDetailsFlow
    ? bookingDetails?.details?.rates?.tripTripFinalPrice
    : selectedRoomSummary?.totalPrice;
  const roomLabel = hasDetailsFlow
    ? null
    : selectedRoomSummary?.roomName;
  const currencySymbol = selectedRoomSummary?.currency === "INR" || !selectedRoomSummary ? "₹" : selectedRoomSummary.currency + " ";

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <Container
        maxWidth={false}
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, md: 4 },
          mt: { xs: 2, md: 4 },
          pt: { xs: 4, md: 8 },
          mx: "auto",
          maxWidth: "1440px",
        }}
      >
        <Paper
          elevation={4}
          sx={{
            p: { xs: 1.5, sm: 2, md: 6 },
            borderRadius: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 2, md: 6 }}
            sx={{ width: "100%" }}
          >
            <Box sx={{ flex: { xs: "1", md: "7.5" } }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  mb: { xs: 2, md: 2 },
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  fontWeight: 700,
                  fontFamily: "sans-serif",
                }}
              >
                Review Booking
              </Typography>
              <Box sx={{ mb: 4 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 1.5, sm: 2 }}
                  alignItems={{
                    xs: "flex-start",
                    sm: "center",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      sx={{ mb: 0.5 }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: {
                            xs: "1.1rem",
                            md: "1.25rem",
                            fontWeight: 500,
                            fontFamily: "sans-serif",
                          },
                        }}
                      >
                        {hotelName || "Hotel"}
                      </Typography>

                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {[...Array(Math.max(0, Math.floor(starRating)))].map((_, index) => (
                            <StarIcon
                              key={index}
                              sx={{
                                color: tokens.highlight,
                                fontSize: { xs: 14, md: 16 },
                              }}
                            />
                          ))}
                        </Box>
                        <Box
                          component="img"
                          src={GoogleIcon}
                          alt="Google"
                          sx={{
                            width: "20px",
                            height: "20px",
                            objectFit: "contain",
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                          {reviewRating ?? "0"}/5
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          ({reviewCount ?? "0"} Reviews)
                        </Typography>
                      </Stack>
                    </Stack>
                    {addressLine && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: "0.8rem", md: "0.875rem" },
                          mt: 0.5,
                          fontFamily: "sans-serif",
                        }}
                      >
                        {addressLine}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={4} sx={{ mt: 1 }}>
   <CalendarMonthIcon fontSize="small" color="action" />
  <Typography variant="body2" sx={{ fontWeight: 500 }}>
    <strong>Check-in:</strong> {checkInDate}
  </Typography>
  <Typography variant="body2" sx={{ fontWeight: 500 }}>
    <strong>Check-out:</strong> {checkOutDate}
  </Typography>
</Stack>
                  </Box>
                  <Box
                    sx={{
                      width: { xs: "100%", sm: 120 },
                      height: { xs: 120, sm: 80 },
                      overflow: "hidden",
                      borderRadius: 1,
                    }}
                  >
                    <img
                      src={passedHotelInfo?.heroImage || passedHotelInfo?.images?.[0] || "/images/payment.png"}
                      alt="Hotel"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                </Stack>
                <Divider sx={{ my: { xs: 4, md: 5 } }} />

                <Box sx={{ mt: 3 }}>
                  {hasDetailsFlow && bookingDetails?.details?.room?.map((room, index) => (
                    <Box key={index}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 500,
                          fontSize: { xs: "0.95rem", md: "1rem" },
                          mb: 0,
                          fontFamily: "sans-serif",
                        }}
                      >
                        {`${room.name} :-- Adults: ${room.occupancy.numOfAdults}, Children: ${room.occupancy.numOfChildren}`}
                      </Typography>

                      {/* Meal Type Display */}
                      {room.category && (
                        <Box sx={{ mt: 1, mb: 2 }}>
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                            {room.category.split(',').map((cat, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  bgcolor: tokens.successSoft,
                                  border: `1px solid ${tokens.border}`,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    bgcolor: tokens.success,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: 0.5,
                                  }}
                                >
                                  <Typography sx={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>✓</Typography>
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: tokens.success,
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {cat.trim().replace(/\//g, ' or ')}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          bgcolor: "rgba(0, 113, 194, 0.04)",
                          borderRadius: 2,
                          mb: 2,
                        }}
                      >
                        {room.description && (
                          <Stack spacing={1.5}>
                            {room.description
                              .replace(/<[^>]*>/g, "")
                              .split("-")
                              .filter((item) => item.trim())
                              .slice(0, 5)
                              .map((feature, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      bgcolor: tokens.brand,
                                      flexShrink: 0,
                                      mt: 1,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "text.primary",
                                      fontSize: { xs: "0.875rem", md: "0.925rem" },
                                      lineHeight: 1.5,
                                      flex: 1,
                                    }}
                                  >
                                    {feature.trim()}
                                  </Typography>
                                </Box>
                              ))}
                          </Stack>
                        )}
                      </Paper>
                    </Box>
                  ))}
                  {hasSelectFlow && roomLabel && (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: "0.95rem", md: "1rem" },
                        fontFamily: "sans-serif",
                      }}
                    >
                      {roomLabel}
                      {selectedRoomSummary?.guestCount != null && ` • ${selectedRoomSummary.guestCount} Guest(s)`}
                    </Typography>
                  )}
                </Box>
              </Box>
              {hasDetailsFlow && (
              <Box sx={{ mt: 2, p: 2, bgcolor: tokens.accentSoft, borderRadius: 2 }}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => setOpenCancellationPolicy(!openCancellationPolicy)}
                >
                  <Typography variant="subtitle2" sx={{ color: "hsl(240deg 1.54% 12.75%)", fontWeight: 600 }}>
                    Cancellation Policy
                  </Typography>
                  <IconButton size="small">
                    {openCancellationPolicy ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </Box>
                <Collapse in={openCancellationPolicy}>
                  {bookingDetails?.details?.cancellationPolicy && bookingDetails.details.cancellationPolicy.length > 0 ? (
                    <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 'none' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Charges</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bookingDetails.details.cancellationPolicy.map((policy, index) => (
                            <TableRow key={index}>
                              <TableCell>{formatDateTime(policy.startDate)}</TableCell>
                              <TableCell>{formatDateTime(policy.endDate)}</TableCell>
                              <TableCell>₹{policy.charges}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ color: 'hsl(240deg 1.54% 12.75%)' }}>
                        No cancellation policy available
                      </Typography>
                      {/* <Typography variant="body2" sx={{ color: 'hsl(240deg 1.54% 12.75%)', fontWeight: 500 }}>
                        Cancellation charges: 100%
                      </Typography> */}
                    </Box>
                  )}
                </Collapse>
              </Box>
              )}
              <Divider sx={{ my: { xs: 4, md: 5 } }} />
              {/* Guest Details Section */}
              <Box sx={{ mt: { xs: 3, md: 4 } }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    mb: { xs: 3, md: 4 },
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                    fontWeight: 500,
                    fontFamily: "sans-serif",
                  }}
                >
                  Enter Guest Details
                </Typography>

                {hasDetailsFlow && bookingDetails?.details?.room ? bookingDetails.details.room.map((room, roomIndex) => (
                  <Box key={roomIndex} sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: tokens.brand }}>
                      Room {roomIndex + 1} - {room.name}
                    </Typography>
                    
                    {guestDetails
                      .filter(guest => guest.roomIndex === roomIndex)
                      .map((guest, guestIndex) => {
                        // Calculate display numbers for adults and children separately
                        const roomGuests = guestDetails.filter(g => g.roomIndex === roomIndex);
                        const adultsInRoom = roomGuests.filter(g => g.guestType === 'adult');
                        const childrenInRoom = roomGuests.filter(g => g.guestType === 'child');
                        
                        let displayNumber;
                        if (guest.guestType === 'adult') {
                          displayNumber = adultsInRoom.findIndex(g => g.guestNumber === guest.guestNumber) + 1;
                        } else {
                          displayNumber = childrenInRoom.findIndex(g => g.guestNumber === guest.guestNumber) + 1;
                        }

                        return (
                          <Box key={guestIndex} sx={{ mb: 4, pl: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold"  sx={{ mb: 2 }}>
                              {guest.guestType === 'adult' ? 'Adult' : 'Child'} {displayNumber}
                              {guest.roomIndex === 0 && guest.guestNumber === 1 && " (Lead Guest)"}
                            </Typography>
                            
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%" }}>
                              <Box sx={{ width: { xs: "100%", sm: "150px" } }}>
                                <FormControl fullWidth>
                                  <Select
                                    value={guest.title}
                                    onChange={(e) => {
                                      const newGuestDetails = [...guestDetails];
                                      const guestIndex = newGuestDetails.findIndex(
                                        g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType
                                      );
                                      newGuestDetails[guestIndex].title = e.target.value;
                                      setGuestDetails(newGuestDetails);
                                    }}
                                    displayEmpty
                                  >
                                    <MenuItem value="" disabled>Select Title</MenuItem>
                                    <MenuItem value="Mr">Mr.</MenuItem>
                                    <MenuItem value="Ms">Ms.</MenuItem>
                                    <MenuItem value="Mrs">Mrs.</MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <TextField
                                  fullWidth
                                  label="First Name"
                                  value={guest.firstName}
                                  onChange={(e) => {
                                    const newGuestDetails = [...guestDetails];
                                    const guestIndex = newGuestDetails.findIndex(
                                      g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType
                                    );
                                    newGuestDetails[guestIndex].firstName = e.target.value;
                                    setGuestDetails(newGuestDetails);
                                  }}
                                />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <TextField
                                  fullWidth
                                  label="Last Name"
                                  value={guest.lastName}
                                  onChange={(e) => {
                                    const newGuestDetails = [...guestDetails];
                                    const guestIndex = newGuestDetails.findIndex(
                                      g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType
                                    );
                                    newGuestDetails[guestIndex].lastName = e.target.value;
                                    setGuestDetails(newGuestDetails);
                                  }}
                                />
                              </Box>
                              {guest.guestType === 'child' && (
                                <Box sx={{ flex: 1 }}>
                                  <TextField
                                    fullWidth
                                    label="Age"
                                    type="number"
                                    value={guest.age}
                                    onChange={(e) => {
                                      const newGuestDetails = [...guestDetails];
                                      const guestIndex = newGuestDetails.findIndex(
                                        g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType
                                      );
                                      newGuestDetails[guestIndex].age = e.target.value;
                                      setGuestDetails(newGuestDetails);
                                    }}
                                    inputProps={{
                                      min: 0,
                                      max: 17,
                                      style: { textAlign: 'center' }
                                    }}
                                    required
                                    // error={guest.guestType === 'child' && !guest.age}
                                    // helperText={guest.guestType === 'child' && !guest.age ? "Age is required for children" : ""}
                                  />
                                </Box>
                              )}
                            </Stack>

                            {guest.roomIndex === 0 && guest.guestNumber === 1 && (
                              <Typography variant="body2" sx={{ mt: 4, mb: 4 }}>
                                Booking details will be sent to this Mobile Number and Email Address
                              </Typography>
                            )}

                            {guest.roomIndex === 0 && guest.guestNumber === 1 && (
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%" }}>
                                <Box sx={{ flex: 1 }}>
                                  <TextField
                                    fullWidth
                                    label="Mobile Number"
                                    value={guest.mobileNumber}
                                    onChange={(e) => {
                                      const newGuestDetails = [...guestDetails];
                                      const guestIndex = newGuestDetails.findIndex(
                                        g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType
                                      );
                                      newGuestDetails[guestIndex].mobileNumber = e.target.value;
                                      setGuestDetails(newGuestDetails);
                                    }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <Box component="span" sx={{ display: "flex", alignItems: "center" }}>
                                            <img
                                              src="https://flagcdn.com/w20/in.png"
                                              alt="India"
                                              style={{ marginRight: 8, width: 20 }}
                                            />
                                            +91
                                          </Box>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <TextField
                                    fullWidth
                                    label="Email ID"
                                    type="email"
                                    value={guest.email}
                                    onChange={(e) => {
                                      const newGuestDetails = [...guestDetails];
                                      const guestIndex = newGuestDetails.findIndex(
                                        g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType
                                      );
                                      newGuestDetails[guestIndex].email = e.target.value;
                                      setGuestDetails(newGuestDetails);
                                    }}
                                  />
                                </Box>
                              </Stack>
                            )}

                            {/* Only show Additional Documents for the first adult in each room */}
                            {guest.roomIndex === 0 && guest.guestNumber === 1 && (
                              <>
                                <Typography variant="body2" sx={{ mt: 4, mb: 2 }}>
                                  Additional Documents
                                </Typography>

                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%" }}>
                                  <Box sx={{ flex: 1 }}>
                                    <TextField
                                      fullWidth
                                      label="PAN Card Number"
                                      value={guest.panCardNumber}
                                      onChange={(e) => {
                                        const newGuestDetails = [...guestDetails];
                                        const guestIndex = newGuestDetails.findIndex(
                                          g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType
                                        );
                                        newGuestDetails[guestIndex].panCardNumber = e.target.value;
                                        setGuestDetails(newGuestDetails);
                                      }}
                                      placeholder="Enter PAN Card Number"
                                      required={bookingDetails?.details?.rules?.isPanCardMandatory}
                                      // error={bookingDetails?.details?.rules?.isPanCardMandatory && !guest.panCardNumber}
                                      // helperText={bookingDetails?.details?.rules?.isPanCardMandatory && !guest.panCardNumber ? "PAN Card Number is required" : ""}
                                      inputProps={{
                                        maxLength: 10,
                                        style: { textTransform: 'uppercase' }
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <TextField
                                      fullWidth
                                      label="Passport Number"
                                      value={guest.passportNumber}
                                      onChange={(e) => {
                                        const newGuestDetails = [...guestDetails];
                                        const guestIndex = newGuestDetails.findIndex(
                                          g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType
                                        );
                                        newGuestDetails[guestIndex].passportNumber = e.target.value;
                                        setGuestDetails(newGuestDetails);
                                      }}
                                      placeholder="Enter Passport Number"
                                      required={bookingDetails?.details?.rules?.isPassportMandatory}
                                      error={bookingDetails?.details?.rules?.isPassportMandatory && !guest.passportNumber}
                                      helperText={bookingDetails?.details?.rules?.isPassportMandatory && !guest.passportNumber ? "Passport Number is required" : ""}
                                      inputProps={{
                                        style: { textTransform: 'uppercase' }
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <TextField
                                      fullWidth
                                      label="Passport Expiry Date"
                                      type="date"
                                      value={guest.passportExpiry}
                                      onChange={(e) => {
                                        const newGuestDetails = [...guestDetails];
                                        const guestIndex = newGuestDetails.findIndex(
                                          g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType
                                        );
                                        newGuestDetails[guestIndex].passportExpiry = e.target.value;
                                        setGuestDetails(newGuestDetails);
                                      }}
                                      required={bookingDetails?.details?.rules?.isPassportMandatory}
                                      error={bookingDetails?.details?.rules?.isPassportMandatory && !guest.passportExpiry}
                                      helperText={bookingDetails?.details?.rules?.isPassportMandatory && !guest.passportExpiry ? "Passport Expiry Date is required" : ""}
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                    />
                                  </Box>
                                </Stack>
                              </>
                            )}
                            
                            {guestIndex < guestDetails.filter(g => g.roomIndex === roomIndex).length - 1 && (
                              <Divider sx={{ my: 4 }} />
                            )}
                          </Box>
                        );
                      })}
                    
                    {roomIndex < bookingDetails.details.room.length - 1 && (
                      <Divider sx={{ my: 4 }} />
                    )}
                  </Box>
                )) : (
                  guestDetails.map((guest, guestIndex) => {
                    const roomIndex = guest.roomIndex;
                    const roomGuests = guestDetails.filter(g => g.roomIndex === roomIndex);
                    const adultsInRoom = roomGuests.filter(g => g.guestType === 'adult');
                    const childrenInRoom = roomGuests.filter(g => g.guestType === 'child');
                    let displayNumber;
                    if (guest.guestType === 'adult') {
                      displayNumber = adultsInRoom.findIndex(g => g.guestNumber === guest.guestNumber) + 1;
                    } else {
                      displayNumber = childrenInRoom.findIndex(g => g.guestNumber === guest.guestNumber) + 1;
                    }
                    return (
                      <Box key={guestIndex} sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: tokens.brand }}>
                          {guest.guestType === 'adult' ? 'Adult' : 'Child'} {displayNumber}
                          {guest.roomIndex === 0 && guest.guestNumber === 1 && " (Lead Guest)"}
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%" }}>
                            <Box sx={{ width: { xs: "100%", sm: "150px" } }}>
                              <FormControl fullWidth>
                                <Select
                                  value={guest.title}
                                  onChange={(e) => {
                                    const newGuestDetails = [...guestDetails];
                                    const idx = newGuestDetails.findIndex(g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType);
                                    if (idx !== -1) newGuestDetails[idx].title = e.target.value;
                                    setGuestDetails(newGuestDetails);
                                  }}
                                  displayEmpty
                                >
                                  <MenuItem value="" disabled>Select Title</MenuItem>
                                  <MenuItem value="Mr">Mr.</MenuItem>
                                  <MenuItem value="Ms">Ms.</MenuItem>
                                  <MenuItem value="Mrs">Mrs.</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <TextField fullWidth label="First Name" value={guest.firstName}
                                onChange={(e) => {
                                  const newGuestDetails = [...guestDetails];
                                  const idx = newGuestDetails.findIndex(g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType);
                                  if (idx !== -1) newGuestDetails[idx].firstName = e.target.value;
                                  setGuestDetails(newGuestDetails);
                                }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <TextField fullWidth label="Last Name" value={guest.lastName}
                                onChange={(e) => {
                                  const newGuestDetails = [...guestDetails];
                                  const idx = newGuestDetails.findIndex(g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType);
                                  if (idx !== -1) newGuestDetails[idx].lastName = e.target.value;
                                  setGuestDetails(newGuestDetails);
                                }} />
                            </Box>
                            {guest.guestType === 'child' && (
                              <Box sx={{ flex: 1 }}>
                                <TextField fullWidth label="Age" type="number" value={guest.age}
                                  onChange={(e) => {
                                    const newGuestDetails = [...guestDetails];
                                    const idx = newGuestDetails.findIndex(g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType);
                                    if (idx !== -1) newGuestDetails[idx].age = e.target.value;
                                    setGuestDetails(newGuestDetails);
                                  }}
                                  inputProps={{ min: 0, max: 17 }} required />
                              </Box>
                            )}
                          </Stack>
                          {guest.roomIndex === 0 && guest.guestNumber === 1 && (
                            <>
                              <Typography variant="body2" sx={{ mt: 2, mb: 2 }}>Booking details will be sent to this Mobile Number and Email Address</Typography>
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%", mt: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                  <TextField fullWidth label="Mobile Number" value={guest.mobileNumber}
                                    onChange={(e) => {
                                      const newGuestDetails = [...guestDetails];
                                      const idx = newGuestDetails.findIndex(g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType);
                                      if (idx !== -1) newGuestDetails[idx].mobileNumber = e.target.value;
                                      setGuestDetails(newGuestDetails);
                                    }}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><Box component="span" sx={{ display: "flex", alignItems: "center" }}><img src="https://flagcdn.com/w20/in.png" alt="India" style={{ marginRight: 8, width: 20 }} />+91</Box></InputAdornment> }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <TextField fullWidth label="Email ID" type="email" value={guest.email}
                                    onChange={(e) => {
                                      const newGuestDetails = [...guestDetails];
                                      const idx = newGuestDetails.findIndex(g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType);
                                      if (idx !== -1) newGuestDetails[idx].email = e.target.value;
                                      setGuestDetails(newGuestDetails);
                                    }} />
                                </Box>
                              </Stack>
                              <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>Additional Documents</Typography>
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%" }}>
                                <Box sx={{ flex: 1 }}>
                                  <TextField fullWidth label="PAN Card Number" value={guest.panCardNumber}
                                    onChange={(e) => {
                                      const newGuestDetails = [...guestDetails];
                                      const idx = newGuestDetails.findIndex(g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType);
                                      if (idx !== -1) newGuestDetails[idx].panCardNumber = e.target.value;
                                      setGuestDetails(newGuestDetails);
                                    }}
                                    placeholder="Enter PAN Card Number" inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <TextField fullWidth label="Passport Number" value={guest.passportNumber}
                                    onChange={(e) => {
                                      const newGuestDetails = [...guestDetails];
                                      const idx = newGuestDetails.findIndex(g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType);
                                      if (idx !== -1) newGuestDetails[idx].passportNumber = e.target.value;
                                      setGuestDetails(newGuestDetails);
                                    }}
                                    placeholder="Enter Passport Number" inputProps={{ style: { textTransform: 'uppercase' } }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <TextField fullWidth label="Passport Expiry Date" type="date" value={guest.passportExpiry}
                                    onChange={(e) => {
                                      const newGuestDetails = [...guestDetails];
                                      const idx = newGuestDetails.findIndex(g => g.roomIndex === roomIndex && g.guestNumber === guest.guestNumber && g.guestType === guest.guestType);
                                      if (idx !== -1) newGuestDetails[idx].passportExpiry = e.target.value;
                                      setGuestDetails(newGuestDetails);
                                    }}
                                    InputLabelProps={{ shrink: true }} />
                                </Box>
                              </Stack>
                            </>
                          )}
                        </Box>
                        {guestIndex < guestDetails.length - 1 && <Divider sx={{ my: 4 }} />}
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
            {/* Right Section - Price Details */}
            <Box
              sx={{
                flex: { xs: "1", md: "4.5" },
                display: "flex",
                justifyContent: "flex-start",
                pl: { md: 4 },
              }}
            >
              <Stack
                spacing={2}
                sx={{
                  width: { xs: "100%", md: "85%" },
                  maxWidth: "400px",
                  ml: { md: 8 },
                }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 2,
                    border: `1px solid ${tokens.border}`,
                    bgcolor: tokens.paper,
                    transition: "box-shadow 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontSize: "1.25rem",
                      fontWeight: 600,
                      color: tokens.brand,
                      mb: 2,
                    }}
                  >
                    Price Details
                  </Typography>
                  <Stack spacing={2}>
                    {hasDetailsFlow ? (
                      <>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2">Base Price</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.95rem" }}>
                            ₹{bookingDetails?.details?.rates?.basePrice || 0}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2">Platform Fee</Typography>
                          <Typography variant="body2">₹{bookingDetails?.details?.rates?.serviceCharges || 0}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2">GST</Typography>
                          <Typography variant="body2">₹{bookingDetails?.details?.rates?.tax?.totalTax || 0}</Typography>
                        </Box>
                        {bookingDetails?.details?.rates?.couponDiscount > 0 && (
                          <Box sx={{ display: "flex", justifyContent: "space-between", color: "success.main" }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>Coupon Discount</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>-₹{bookingDetails?.details?.rates?.couponDiscount}</Typography>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2">Room total</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{currencySymbol}{totalAmount ?? 0}</Typography>
                      </Box>
                    )}
                    <Divider />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600, color: tokens.brand }}>
                        Total Amount
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600, color: tokens.success }}>
                        {currencySymbol}{totalAmount ?? 0}
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={handleProceedToPayClick}
                      disabled={isLoading}
                      sx={{
                        mt: 3,
                        borderRadius: "30px",
                        py: 1,
                        bgcolor: tokens.brand,
                        fontSize: "1rem",
                        fontWeight: 500,
                        textTransform: "none",
                        "&:hover": {
                          bgcolor: tokens.brandDeep,
                        },
                      }}
                    >
                      {isLoading ? <Loader variant="inline" /> : "Proceed to Pay"}
                    </Button>
                  </Stack>
                </Paper>
                <Paper
                  elevation={2}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 2,
                    border: `1px solid ${tokens.border}`,
                    bgcolor: tokens.paper,
                    transition: "box-shadow 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontSize: "1.25rem",
                      fontWeight: 600,
                      color: tokens.brand,
                      mb: 2,
                    }}
                  >
                    Apply Coupon Code
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Enter Coupon Code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: tokens.accent,
                          },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleApplyCoupon()}
                      disabled={isCouponLoading}
                      sx={{
                        borderRadius: "30px",
                        py: 1,
                        bgcolor: tokens.brand,
                        fontSize: "0.95rem",
                        fontWeight: 500,
                        textTransform: "none",
                        "&:hover": {
                          bgcolor: tokens.brandDeep,
                        },
                      }}
                    >
                      {isCouponLoading ? <Loader variant="inline" /> : "Apply"}
                    </Button>
                  </Stack>

                  {availableCoupons && availableCoupons.length > 0 && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: "rgba(0, 113, 194, 0.04)",
                        borderRadius: 2,
                        border: "1px solid rgba(0, 113, 194, 0.1)",
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                        Available Coupons
                      </Typography>
                      {availableCoupons.map((coupon) => (
                        <Box
                          key={coupon.couponId}
                          sx={{ p: 2, bgcolor: tokens.surface, borderRadius: 1, mb: 2 }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <LocalOfferIcon color="primary" />
                              <Typography variant="body1" fontWeight="bold">
                                {coupon.couponCode}
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleApplyCoupon(coupon.couponCode)}
                              disabled={isCouponLoading}
                            >
                              {isCouponLoading ? <Loader variant="inline" /> : "Apply"}
                            </Button>
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {coupon.couponName}
                          </Typography>
                          <Typography variant="body2" color="success.main">
                            Save{" "}
                            {coupon.categorySymbol === "Percentage"
                              ? `${coupon.couponValue}%`
                              : `₹${coupon.couponValue}`}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Stack>
            </Box>
          </Stack>
        </Paper>
        {appliedCoupon && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="success.main">
              Coupon Applied: {appliedCoupon.couponCode} - {appliedCoupon.couponName}
            </Typography>
            <Typography variant="body2" color="success.main">
              Value: {appliedCoupon.categorySymbol === "Percentage"
                ? `${appliedCoupon.couponValue}%`
                : `₹${appliedCoupon.couponValue}`}
            </Typography>
          </Box>
        )}
      </Container>
      {/* <Modal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        aria-labelledby="payment-method-modal"
        aria-describedby="select-payment-method"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            outline: 'none',
          }}
        >
          <Typography id="payment-method-modal" variant="h6" sx={{ mb: 2 }}>
            Please select payment service
          </Typography>
          <RadioGroup
            value={selectedPaymentMethod}
            onChange={(e) => setSelectedPaymentMethod(e.target.value)}
            sx={{ rowGap: 3 }}
          >
            <FormControlLabel
              value="HDFC"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <img src="https://logosmarcas.net/wp-content/uploads/2020/11/HDFC-Bank-Emblema.png" alt="HDFC Bank" style={{ width: 150, height: 60 }} />
                </Box>
              }
            />
            <FormControlLabel
              value="PHONEPE"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <img src="https://i1.wp.com/logos-download.com/wp-content/uploads/2021/01/PhonePe_Logo_full.png" alt="PhonePe" style={{ width: 150, height: 35 }} />
                  
                </Box>
              }
            />
          </RadioGroup>
          <Stack direction="row" spacing={2} sx={{ mt: 3, justifyContent: 'flex-end' }}>
            <Button onClick={() => setPaymentModalOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!selectedPaymentMethod}
              onClick={() => {
                setPaymentModalOpen(false);
                handleProceedToPay(selectedPaymentMethod);
              }}
            >
              Proceed
            </Button>
          </Stack>
        </Box>
      </Modal> */}
      <Modal
  open={paymentModalOpen}
  onClose={() => setPaymentModalOpen(false)}
  aria-labelledby="payment-method-modal"
  aria-describedby="select-payment-method"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: { xs: '90vw', sm: 500 },
      maxWidth: { xs: '90vw', sm: 500 },
      maxHeight: { xs: '90vh', sm: 'auto' },
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      outline: 'none',
      overflow: 'auto',
    }}
  >
    {/* Header */}
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
      <Typography 
        id="payment-method-modal" 
        variant="h6" 
        sx={{ 
          fontWeight: 600,
          color: 'text.primary',
          mb: 1,
          fontSize: { xs: '1.1rem', sm: '1.25rem' }
        }}
      >
        Please select payment service
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          color: 'text.secondary',
          fontSize: { xs: '0.875rem', sm: '0.875rem' }
        }}
      >
        Choose your preferred payment service to continue
      </Typography>
    </Box>

    {/* Content */}
    <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
      <RadioGroup
        value={selectedPaymentMethod}
        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
        sx={{ gap: { xs: 1, sm: 1.5 } }}
      >
        {/* HDFC Option */}
        <FormControlLabel
          value="HDFC"
          control={
            <Radio 
              sx={{ 
                p: 1,
                '&.Mui-checked': { color: 'primary.main' } 
              }} 
            />
          }
          label={
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                flex: 1,
                p: { xs: 1, sm: 1.5 },
                ml: 1,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: selectedPaymentMethod === 'HDFC' ? 'primary.main' : 'grey.300',
                bgcolor: selectedPaymentMethod === 'HDFC' ? 'primary.50' : 'grey.50',
                transition: 'all 0.2s',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50',
                }
              }}
            >
              <Box
                sx={{
                  width: { xs: 35, sm: 40 },
                  height: { xs: 35, sm: 40 },
                  borderRadius: 1,
                  bgcolor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: { xs: 1.5, sm: 2 },
                  flexShrink: 0,
                }}
              >
                <img 
                  src="https://logosmarcas.net/wp-content/uploads/2020/11/HDFC-Bank-Emblema.png" 
                  alt="HDFC Bank" 
                  style={{ width: '100%', height: 'auto', maxWidth: 50 }} 
                />
              </Box>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  HDFC Bank
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
                  Net Banking
                </Typography>
              </Box>
            </Box>
          }
          sx={{ 
            m: 0,
            display: 'flex',
            alignItems: 'center',
            '& .MuiFormControlLabel-label': { 
              flex: 1,
              ml: 0
            }
          }}
        />

        {/* PhonePe Option */}
        <FormControlLabel
          value="PHONEPE"
          control={
            <Radio 
              sx={{ 
                p: 1,
                '&.Mui-checked': { color: 'primary.main' } 
              }} 
            />
          }
          label={
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                flex: 1,
                p: { xs: 1, sm: 1.5 },
                ml: 1,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: selectedPaymentMethod === 'PHONEPE' ? 'primary.main' : 'grey.300',
                bgcolor: selectedPaymentMethod === 'PHONEPE' ? 'primary.50' : 'grey.50',
                transition: 'all 0.2s',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50',
                }
              }}
            >
              <Box
                sx={{
                  width: { xs: 35, sm: 40 },
                  height: { xs: 35, sm: 40 },
                  borderRadius: 1,
                  bgcolor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: { xs: 1.5, sm: 2 },
                  flexShrink: 0,
                }}
              >
                <img 
                  src="https://i1.wp.com/logos-download.com/wp-content/uploads/2021/01/PhonePe_Logo_full.png" 
                  alt="PhonePe" 
                  style={{ width: '100%', height: 'auto', maxWidth: 50 }} 
                />
              </Box>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  PhonePe
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
                  UPI Payment
                </Typography>
              </Box>
            </Box>
          }
          sx={{ 
            m: 0,
            display: 'flex',
            alignItems: 'center',
            '& .MuiFormControlLabel-label': { 
              flex: 1,
              ml: 0
            }
          }}
        />
      </RadioGroup>
    </Box>

    {/* Footer */}
    <Box sx={{ p: { xs: 2, sm: 3 }, pt: 2 }}>
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={{ xs: 1.5, sm: 2 }} 
sx={{ 
      justifyContent: 'flex-end',
      '& > *': {
        width: { xs: '100%', sm: 'auto' }
      },
      '& > :first-of-type': {
        mt: { xs: '10px', sm: 0 } // Applies extra margin-top to Cancel button on mobile
      }
    }}
      >
        <Button 
          onClick={() => setPaymentModalOpen(false)} 
          variant="outlined"
          size="medium"
          sx={{
            px: 3,
            textTransform: 'none',
            order: { xs: 2, sm: 1 },
          }}
          
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selectedPaymentMethod}
          onClick={() => {
            setPaymentModalOpen(false);
            handleProceedToPay(selectedPaymentMethod);
          }}
          size="medium"
          sx={{
            px: 3,
            textTransform: 'none',
            fontWeight: 500,
            order: { xs: 1, sm: 2 }
          }}
        >
          Proceed to Pay
        </Button>
      </Stack>
    </Box>
  </Box>
</Modal>
    </>
  );
};

export default Payment;
